/**
 * ReviewDrawer — the LAB-ACCESS-REQUEST review dossier. A side panel over one request: its details, an
 * email-template preview of what the prospect will receive on the next transition, and the decision panel —
 * the funnel state machine surfaced as buttons (correct-by-reissue, version-guarded):
 *
 *     demande            → Prendre en revue (→ en_revue)  · Approuver  · Rejeter
 *     en_revue           → Approuver  · Rejeter
 *     approuvee|rejetee  → terminal (read-only; shows the outcome + who/when)
 *
 * The transition IS the email send (the server queues the templated mail on commit — no separate endpoint),
 * so the drawer previews the outgoing message inline via <EmailPreview>. Approve opens the provision
 * governance form (reused blocks from onboarding.types); Reject requires a raison. Every write passes
 * `expected_version` for optimistic concurrency (a stale row → typed 409 the panel surfaces inline). The
 * server is the trust boundary; this panel only guides/disables UI. Token-driven, AA-contrast, RTL-safe.
 */
import { useEffect, useState } from "react";
import { Badge, Button, Drawer, FormField, Input, Select, Textarea, useToast } from "@/ui";
import { ApiError } from "@/api";
import { useT } from "@/app";
import { formatDateTime } from "../lib";
import { demandeStatutTone } from "../lib";
import { EmailPreview } from "./EmailPreview";
import { useApproveRequest, useRejectRequest, useReviewRequest } from "../screens/onboardingRequests.api";
import type { DemandeStatut, LabAccessRequestRow } from "../screens/onboardingRequests.types";
import type { DeployClass, TenancyTier } from "../screens/onboarding.types";

export interface ReviewDrawerProps {
  request: LabAccessRequestRow | null;
  onClose: () => void;
}

/** The active decision sub-mode inside the drawer (null = the dossier + decision buttons). */
type DecisionMode = "approve" | "reject" | null;

const DEPLOY_CLASSES: ReadonlyArray<DeployClass> = ["local", "cloud", "hybrid"];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Which email template previews on the NEXT transition, given the current statut. */
function nextPreviewKind(statut: DemandeStatut): "en_revue" | "approuvee" | null {
  if (statut === "demande") return "en_revue";
  if (statut === "en_revue") return "approuvee";
  return null;
}

export function ReviewDrawer({ request, onClose }: ReviewDrawerProps) {
  const { t, locale } = useT();
  const toast = useToast();
  const review = useReviewRequest();
  const approve = useApproveRequest();
  const reject = useRejectRequest();

  const [mode, setMode] = useState<DecisionMode>(null);
  const [error, setError] = useState<string | null>(null);

  // review note
  const [note, setNote] = useState("");
  // reject reason
  const [reason, setReason] = useState("");
  // approve / provision form (governance fields the intake could not carry)
  const [reference, setReference] = useState("");
  const [raisonSociale, setRaisonSociale] = useState("");
  const [nomCommercial, setNomCommercial] = useState("");
  const [deployClass, setDeployClass] = useState<DeployClass>("cloud");
  const [tenancyTier, setTenancyTier] = useState<TenancyTier>("partage");
  const [prenom, setPrenom] = useState("");
  const [nom, setNom] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [password, setPassword] = useState("");

  const open = request !== null;
  const busy = review.isPending || approve.isPending || reject.isPending;

  // Reset the sub-mode + form when a NEW request opens (or the drawer closes). Prefill from the dossier so
  // the operator starts from the prospect's own values.
  useEffect(() => {
    setMode(null);
    setError(null);
    setNote("");
    setReason("");
    setReference("");
    setRaisonSociale(request?.nomLabo ?? "");
    setNomCommercial(request?.nomLabo ?? "");
    setDeployClass("cloud");
    setTenancyTier("partage");
    setPrenom("");
    setNom("");
    setOwnerEmail(request?.email ?? "");
    setPassword("");
  }, [request?.id, request?.nomLabo, request?.email]);

  if (!request) return <Drawer open={false} onClose={onClose} title="" children={null} />;

  const isTerminal = request.statut === "approuvee" || request.statut === "rejetee";
  const previewKind = nextPreviewKind(request.statut);
  const mailVars = {
    reference: request.reference,
    nomLabo: request.nomLabo,
    wilaya: request.wilaya,
    raisonRejet: reason || request.raisonRejet || "",
  };

  const close = () => {
    if (busy) return;
    onClose();
  };

  const onApiError = (err: unknown) =>
    setError(err instanceof ApiError ? err.message : t("mgmt.reqs.error.generic"));

  const submitReview = () => {
    setError(null);
    review.mutate(
      { id: request.id, ...(note.trim() ? { note: note.trim() } : {}), expected_version: request.version },
      {
        onSuccess: () => {
          toast.success(t("mgmt.reqs.toast.reviewed"));
          onClose();
        },
        onError: onApiError,
      },
    );
  };

  const submitReject = () => {
    setError(null);
    if (!reason.trim()) {
      setError(t("mgmt.reqs.reject.reason.required"));
      return;
    }
    reject.mutate(
      { id: request.id, raison_rejet: reason.trim(), expected_version: request.version },
      {
        onSuccess: () => {
          toast.success(t("mgmt.reqs.toast.rejected"));
          onClose();
        },
        onError: onApiError,
      },
    );
  };

  const submitApprove = () => {
    setError(null);
    if (
      !reference.trim() ||
      !raisonSociale.trim() ||
      !nomCommercial.trim() ||
      !prenom.trim() ||
      !nom.trim() ||
      !ownerEmail.trim() ||
      !password
    ) {
      setError(t("mgmt.reqs.field.required"));
      return;
    }
    if (!EMAIL_RE.test(ownerEmail.trim())) {
      setError(t("mgmt.reqs.field.invalidEmail"));
      return;
    }
    approve.mutate(
      {
        id: request.id,
        laboratoire: {
          reference: reference.trim(),
          raison_sociale: raisonSociale.trim(),
          nom_commercial: nomCommercial.trim(),
          deploy_class: deployClass,
          tenancy_tier: tenancyTier,
          ...(request.wilaya ? { wilaya: request.wilaya } : {}),
        },
        owner: { prenom: prenom.trim(), nom: nom.trim(), email: ownerEmail.trim(), password },
        expected_version: request.version,
      },
      {
        onSuccess: (res) => {
          toast.success(
            t("mgmt.reqs.toast.approved", {
              name: res.provision.laboratoire.raisonSociale ?? res.provision.laboratoire.reference ?? "",
            }),
          );
          onClose();
        },
        onError: onApiError,
      },
    );
  };

  // ── Footer buttons vary by mode + statut ──────────────────────────────────────────
  let footer: React.ReactNode;
  if (mode === "approve") {
    footer = (
      <>
        <Button variant="ghost" onClick={() => setMode(null)} disabled={busy}>
          {t("mgmt.reqs.action.back")}
        </Button>
        <Button onClick={submitApprove} loading={approve.isPending}>
          {t("mgmt.reqs.approve.submit")}
        </Button>
      </>
    );
  } else if (mode === "reject") {
    footer = (
      <>
        <Button variant="ghost" onClick={() => setMode(null)} disabled={busy}>
          {t("mgmt.reqs.action.back")}
        </Button>
        <Button variant="danger" onClick={submitReject} loading={reject.isPending}>
          {t("mgmt.reqs.reject.submit")}
        </Button>
      </>
    );
  } else if (isTerminal) {
    footer = (
      <Button variant="ghost" onClick={close}>
        {t("mgmt.reqs.action.close")}
      </Button>
    );
  } else {
    footer = (
      <>
        <Button variant="ghost" onClick={close} disabled={busy}>
          {t("mgmt.reqs.action.cancel")}
        </Button>
        {request.statut === "demande" && (
          <Button variant="secondary" onClick={submitReview} loading={review.isPending}>
            {t("mgmt.reqs.action.take")}
          </Button>
        )}
        <Button variant="danger" onClick={() => setMode("reject")} disabled={busy}>
          {t("mgmt.reqs.action.reject")}
        </Button>
        <Button onClick={() => setMode("approve")} disabled={busy}>
          {t("mgmt.reqs.action.approve")}
        </Button>
      </>
    );
  }

  const dt = (label: string, value: React.ReactNode) => (
    <>
      <dt style={{ fontSize: "var(--nv-fs-caption)", color: "var(--nv-text-3, var(--nv-steel))" }}>{label}</dt>
      <dd style={{ margin: 0 }}>{value}</dd>
    </>
  );

  return (
    <Drawer
      open={open}
      onClose={close}
      title={t("mgmt.reqs.drawer.title")}
      footer={footer}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)" }}>
        {/* ── Dossier (always visible) ────────────────────────────────── */}
        <section aria-label={t("mgmt.reqs.drawer.dossier")}>
          <div style={{ display: "flex", alignItems: "center", gap: "var(--nv-s-2)", marginBottom: "var(--nv-s-3)" }}>
            <Badge tone={demandeStatutTone(request.statut)} glyph="●">
              {t(`mgmt.reqs.statut.${request.statut}`)}
            </Badge>
            <span className="nv-ltr-island" style={{ fontFamily: "var(--nv-font-mono)", fontSize: "var(--nv-fs-caption)" }}>
              {request.reference}
            </span>
          </div>
          <dl style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "var(--nv-s-2) var(--nv-s-4)", margin: 0 }}>
            {dt(t("mgmt.reqs.field.lab"), request.nomLabo)}
            {dt(t("mgmt.reqs.field.wilaya"), request.wilaya)}
            {dt(t("mgmt.reqs.field.email"), <span className="nv-ltr-island">{request.email}</span>)}
            {dt(
              t("mgmt.reqs.field.telephone"),
              request.telephone ? <span className="nv-ltr-island">{request.telephone}</span> : t("mgmt.reqs.field.none"),
            )}
            {dt(t("mgmt.reqs.field.received"), <span className="nv-ltr-island">{formatDateTime(request.createdAt, locale)}</span>)}
            {request.message && dt(t("mgmt.reqs.field.message"), <span style={{ whiteSpace: "pre-wrap" }}>{request.message}</span>)}
            {isTerminal &&
              dt(t("mgmt.reqs.field.reviewedAt"), <span className="nv-ltr-island">{formatDateTime(request.reviewedAt, locale)}</span>)}
            {request.statut === "rejetee" &&
              request.raisonRejet &&
              dt(t("mgmt.reqs.field.raisonRejet"), <span style={{ whiteSpace: "pre-wrap" }}>{request.raisonRejet}</span>)}
            {request.statut === "approuvee" &&
              request.laboratoireId &&
              dt(t("mgmt.reqs.field.provisioned"), <span className="nv-ltr-island">#{request.laboratoireId}</span>)}
          </dl>
        </section>

        {/* ── Decision area ───────────────────────────────────────────── */}
        {isTerminal ? (
          <p style={{ margin: 0, color: "var(--nv-text-2, var(--nv-slate))" }}>{t("mgmt.reqs.decision.terminal")}</p>
        ) : mode === "approve" ? (
          <fieldset style={{ border: 0, margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: "var(--nv-s-3)" }}>
            <legend style={{ padding: 0, fontSize: "var(--nv-fs-label)", fontWeight: 600, color: "var(--nv-text-2, var(--nv-slate))" }}>
              {t("mgmt.reqs.approve.title")}
            </legend>
            <p style={{ margin: 0, fontSize: "var(--nv-fs-caption)", color: "var(--nv-text-2, var(--nv-slate))" }}>
              {t("mgmt.reqs.approve.intro")}
            </p>
            <div style={{ display: "grid", gap: "var(--nv-s-3)", gridTemplateColumns: "1fr 1fr" }}>
              <FormField label={t("mgmt.reqs.approve.reference")} required>
                {(p) => <Input {...p} value={reference} onChange={(e) => setReference(e.target.value)} autoComplete="off" />}
              </FormField>
              <FormField label={t("mgmt.reqs.approve.deployClass")} required>
                {(p) => (
                  <Select
                    {...p}
                    value={deployClass}
                    onChange={(e) => setDeployClass(e.target.value as DeployClass)}
                    options={DEPLOY_CLASSES.map((d) => ({ value: d, label: d }))}
                  />
                )}
              </FormField>
            </div>
            <FormField label={t("mgmt.reqs.approve.raisonSociale")} required>
              {(p) => <Input {...p} value={raisonSociale} onChange={(e) => setRaisonSociale(e.target.value)} />}
            </FormField>
            <FormField label={t("mgmt.reqs.approve.nomCommercial")} required>
              {(p) => <Input {...p} value={nomCommercial} onChange={(e) => setNomCommercial(e.target.value)} />}
            </FormField>
            <FormField label={t("mgmt.reqs.approve.tenancyTier")}>
              {(p) => (
                <Select
                  {...p}
                  value={tenancyTier}
                  onChange={(e) => setTenancyTier(e.target.value as TenancyTier)}
                  options={[
                    { value: "partage", label: t("mgmt.reqs.approve.tenancy.partage") },
                    { value: "dedie", label: t("mgmt.reqs.approve.tenancy.dedie") },
                  ]}
                />
              )}
            </FormField>
            <div style={{ display: "grid", gap: "var(--nv-s-3)", gridTemplateColumns: "1fr 1fr" }}>
              <FormField label={t("mgmt.reqs.approve.owner.prenom")} required>
                {(p) => <Input {...p} value={prenom} onChange={(e) => setPrenom(e.target.value)} />}
              </FormField>
              <FormField label={t("mgmt.reqs.approve.owner.nom")} required>
                {(p) => <Input {...p} value={nom} onChange={(e) => setNom(e.target.value)} />}
              </FormField>
            </div>
            <FormField label={t("mgmt.reqs.approve.owner.email")} required>
              {(p) => <Input {...p} type="email" value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)} autoComplete="off" />}
            </FormField>
            <FormField label={t("mgmt.reqs.approve.owner.password")} required>
              {(p) => (
                <Input {...p} type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
              )}
            </FormField>
            <EmailPreview kind="approuvee" to={request.email} vars={mailVars} />
          </fieldset>
        ) : mode === "reject" ? (
          <fieldset style={{ border: 0, margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: "var(--nv-s-3)" }}>
            <legend style={{ padding: 0, fontSize: "var(--nv-fs-label)", fontWeight: 600, color: "var(--nv-text-2, var(--nv-slate))" }}>
              {t("mgmt.reqs.reject.title")}
            </legend>
            <FormField label={t("mgmt.reqs.reject.reason")} required>
              {(p) => (
                <Textarea
                  {...p}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={t("mgmt.reqs.reject.reason.placeholder")}
                />
              )}
            </FormField>
            <EmailPreview kind="rejetee" to={request.email} vars={mailVars} />
          </fieldset>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-3)" }}>
            <h3 style={{ margin: 0, fontSize: "var(--nv-fs-label)", fontWeight: 600, color: "var(--nv-text-2, var(--nv-slate))" }}>
              {t("mgmt.reqs.decision.title")}
            </h3>
            {request.statut === "demande" && (
              <FormField label={t("mgmt.reqs.decision.note")}>
                {(p) => (
                  <Textarea
                    {...p}
                    rows={2}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder={t("mgmt.reqs.decision.note.placeholder")}
                  />
                )}
              </FormField>
            )}
            {previewKind && <EmailPreview kind={previewKind} to={request.email} vars={mailVars} />}
          </div>
        )}

        {error && (
          <p role="alert" style={{ margin: 0, color: "var(--nv-danger, var(--nv-critical))" }}>
            {error}
          </p>
        )}
      </div>
    </Drawer>
  );
}
